import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Earth3D from "@/components/Earth3D";
import LanguageToggle from "@/components/LanguageToggle";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Satellite as SatIcon, ShieldCheck, Radio, Globe2 } from "lucide-react";

const Stat = ({ k, v, testid }) => (
  <div className="px-6 py-4 holo-panel min-w-[140px]" data-testid={testid}>
    <div className="label-overline">{k}</div>
    <div className="font-display text-xl font-black mt-1 text-white">{v}</div>
  </div>
);

export default function Landing() {
  const { t } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({ total_transmissions: 0, countries: 0, active_missions: 0 });

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/missions/globe`)
      .then((r) => r.json()).then((d) => d?.stats && setStats(d.stats)).catch(() => {});
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden text-white">
      {/* 3D background */}
      <Earth3D className="absolute inset-0" />

      {/* Top dark vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 0%, rgba(3,4,11,0.7) 70%, rgba(3,4,11,0.95) 100%)" }} />

      {/* Header */}
      <header className="relative z-10 px-6 lg:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="landing-logo">
          <img src="/s2s-logo-sm.png" alt="S2S — Sounds To Space"
               className="h-36 lg:h-44 w-auto select-none pointer-events-none"
               style={{ filter: "drop-shadow(0 0 14px rgba(0,212,255,0.45))" }} />
        </div>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          {user ? (
            <Link to="/dashboard" data-testid="landing-dashboard-link" className="btn-secondary-s2s !py-2 !text-[11px]">{t("nav.dashboard")}</Link>
          ) : (
            <>
              <Link to="/login" data-testid="landing-login-link" className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-white/70 hover:text-white">{t("nav.login")}</Link>
              <Link to="/register" data-testid="landing-register-link" className="btn-primary-s2s !py-2 !text-[11px]">{t("nav.register")}</Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 lg:px-16 pt-12 lg:pt-20 pb-24 max-w-7xl">
        <div className="label-overline mb-6 flex items-center gap-2">
          <Radio size={12} className="blink" /> {t("tagline")}
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter max-w-3xl" data-testid="hero-title">
          {t("hero_title")}
        </h1>
        <p className="mt-6 max-w-xl text-white/70 text-base leading-relaxed">{t("hero_sub")}</p>

        <div className="mt-10 flex flex-wrap gap-3">
          <button
            data-testid="cta-start-transmission"
            onClick={() => nav(user ? "/upload" : "/register")}
            className="btn-primary-s2s"
          >
            {t("start")} <ArrowRight size={16} />
          </button>
          <Link to={user ? "/galaxy" : "/globe"} data-testid="cta-explore" className="btn-secondary-s2s">
            {t("explore")}
          </Link>
        </div>

        <div className="mt-14 flex flex-wrap gap-3" data-testid="landing-stats">
          <Stat k={t("globe.total")} v={stats.total_transmissions} testid="stat-tx" />
          <Stat k={t("globe.active")} v={stats.active_missions} testid="stat-active" />
          <Stat k={t("globe.countries")} v={stats.countries} testid="stat-countries" />
        </div>
      </section>

      {/* Pillars */}
      <section className="relative z-10 px-6 lg:px-16 py-20 max-w-7xl">
        <div className="divider-cyan mb-12" />
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: SatIcon, title: t("pillars.deepspace_title"), desc: t("pillars.deepspace_desc") },
            { icon: ShieldCheck, title: t("pillars.vault_title"), desc: t("pillars.vault_desc") },
            { icon: Globe2, title: t("pillars.map_title"), desc: t("pillars.map_desc") },
          ].map((p, i) => (
            <div key={i} className="holo-panel p-8 transition-all">
              <p.icon size={20} className="text-[#00D4FF]" />
              <h3 className="font-display font-bold text-lg mt-4 tracking-tight">{p.title}</h3>
              <p className="text-white/65 text-sm mt-2 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 lg:px-16 py-10 border-t border-white/5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono-data text-[11px] tracking-[0.2em] uppercase text-white/40">
            © {new Date().getFullYear()} S2S — {t("footer.copyright")}
          </div>
          <div className="flex gap-6 font-mono-data text-[11px] tracking-[0.2em] uppercase text-white/60">
            <Link to="/pricing" data-testid="footer-plans">{t("footer.plans")}</Link>
            <Link to="/globe" data-testid="footer-globe">{t("footer.globe")}</Link>
            <Link to="/register" data-testid="footer-enlist">{t("footer.enlist")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
